// src/features/monetize/components/AddPriceModal.tsx

import { useEffect } from 'react';
import { Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useController, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MONETIZE_CONSTRAINTS } from '@/lib/constants/monetize';
import type { MonetizeProduct } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { priceFormSchema, type PriceFormValues } from '../schemas/monetizeSchemas';
import { useAddPrice } from '../useMonetizeMutations';

interface AddPriceModalProps {
  product: MonetizeProduct | null;
  onClose: () => void;
}

export function AddPriceModal({ product, onClose }: AddPriceModalProps) {
  const addPrice = useAddPrice();
  const form = useForm<PriceFormValues>({ resolver: zodResolver(priceFormSchema), defaultValues: MonetizeUtils.defaultPriceForm('subscription') });
  const intervals = product ? MonetizeUtils.intervalsFor(product.type) : [];

  useEffect(() => {
    if (product) form.reset(MonetizeUtils.defaultPriceForm(product.type));
  }, [product, form]);

  async function handleSubmit(values: PriceFormValues) {
    if (!product) return;
    try {
      await addPrice.mutateAsync({ productId: product.id, req: MonetizeUtils.formToPriceReq(values) });
      notifications.show({ title: 'Price added', message: `${MonetizeUtils.priceLabel(MonetizeUtils.formToPriceReq(values))} is offered for "${product.name}".`, color: 'green' });
      onClose();
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Failed to add the price.'), color: 'red' });
    }
  }

  return (
    <Modal opened={product !== null} onClose={onClose} title={product ? `Add a price to ${product.name}` : ''} size="md">
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="md">
          <Group grow align="flex-start">
            <Amount control={form.control} />
            <Interval control={form.control} intervals={intervals} />
          </Group>
          {product?.type === 'subscription' && <Trial control={form.control} />}
          <Nickname control={form.control} />
          <Text size="xs" c="dimmed">A price can be archived later; viewers who bought it keep it.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={addPrice.isPending}>Add price</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

type C = Control<PriceFormValues>;

function Amount({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'amount' });
  return <NumberInput label="Amount" prefix="$" decimalScale={2} fixedDecimalScale min={0.5} value={field.value} onChange={field.onChange} error={fieldState.error?.message} />;
}

function Interval({ control, intervals }: { control: C; intervals: readonly string[] }) {
  const { field, fieldState } = useController({ control, name: 'interval' });
  return (
    <Select
      label="Billing"
      data={intervals.map((i) => ({ value: i, label: MonetizeUtils.intervalLabel(i as PriceFormValues['interval']) }))}
      value={field.value}
      onChange={(v) => v && field.onChange(v)}
      allowDeselect={false}
      error={fieldState.error?.message}
    />
  );
}

function Trial({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'trialDays' });
  return <NumberInput label="Free trial" suffix=" days" min={0} max={MONETIZE_CONSTRAINTS.MAX_TRIAL_DAYS} allowDecimal={false} value={field.value} onChange={(v) => field.onChange(typeof v === 'number' ? v : 0)} error={fieldState.error?.message} maw={200} />;
}

function Nickname({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'nickname' });
  return <TextInput label="Label" placeholder="Monthly" maxLength={MONETIZE_CONSTRAINTS.NICKNAME_MAX} value={field.value} onChange={field.onChange} error={fieldState.error?.message} />;
}
